import dayjs from "dayjs";
import { DishSalesForecast } from "../types/dishSalesForecast";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "./endpoints";
import { baseFetch, HttpVerbs } from "./baseFetch";
import { isError } from "../utilities/typeguards";

/**
 * Generates an array of DishSalesForecast objects for each day between the specified start and end dates.
 *
 * @param {string} startDate - The start date in string format.
 * @param {string} endDate - The end date in string format.
 * @param {DishSalesForecast} baseObject - The base DishSalesForecast object to be used as a template for each generated forecast.
 * @returns {DishSalesForecast[]} An array of DishSalesForecast objects, each representing a forecast for a single day.
 */
const generateForecasts = (
  startDate: string,
  endDate: string,
  baseObject: DishSalesForecast
): DishSalesForecast[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const forecasts: DishSalesForecast[] = [];

  for (
    let d = start;
    d.isBefore(end) || d.isSame(end, "day");
    d = d.add(1, "day")
  ) {
    const newStartTime = d.startOf("day").toDate();
    const newEndTime = d.add(1, "day").startOf("day").toDate();
    const newWeekDay = d.format("dddd"); // Get the day of the week
    const newLastUpdated = d.add(-7, "day").toDate();

    const updatedObject: DishSalesForecast = {
      ...baseObject,
      StartTime: newStartTime,
      EndTime: newEndTime,
      WeekDay: newWeekDay,
      LastUpdated: newLastUpdated,
    };

    forecasts.push(updatedObject);
  }

  return forecasts;
};

type FetchForecastParams = {
  mdrSiteId: number;
  siteId: string;
  orgId: string;
  searchPeriod: { from: string; to: string };
};

const fetchForecast = async (
  params: FetchForecastParams
): Promise<DishSalesForecast[]> => {
  try {
    const url = `${endpoints.calculateDishSalesForecastByDate}?code=${process.env.REACT_APP_CALCULATE_DISH_SALES_FORECAST_BY_DATE}&regionID=1&siteId=${params.mdrSiteId}&dateFrom=${params.searchPeriod.from}&dateTo=${params.searchPeriod.to}`;

    const response = await baseFetch<DishSalesForecast>({
        endpointUrl: url,
        httpVerb: HttpVerbs.GET,
        orgId: params.orgId,
    });
    if (isError(response)) {
      throw new Error(`Error fetching aggregated sales data ${response}`);
    }

    return generateForecasts(
      params.searchPeriod.from,
      params.searchPeriod.to,
      response
    );
} catch (error) {
    console.error("Error fetching forecasts:", error);
    throw error;
  }
};

const useForecastQuery = (params: FetchForecastParams) => {
  return useQuery({
    queryKey: [
      "forecast",
      params.siteId,
      params.searchPeriod.from,
      params.searchPeriod.to,
    ],
    queryFn: () =>
      fetchForecast({
        mdrSiteId: params.mdrSiteId,
        siteId: params.siteId,
        orgId: params.orgId,
        searchPeriod: params.searchPeriod,
      }),
    // Consider data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep unused data in cache for 30 minutes
    gcTime: 30 * 60 * 1000,
  });
};

export { useForecastQuery };
export type { FetchForecastParams };
